import React from "react";
import { DateTime } from "luxon";
import SpecialistsView from "./SpecialistsView";
import WaitlistDialog from "./WaitlistDialog";
import { getWaitlistConfig } from "../../../waitlistConfigStore";
import { WidgetHeader } from "../../../components/WidgetHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { salonNow } from "@/lib/time";
import { useAvailabilityForDates } from "@/hooks/calendar/useAvailabilityForDates";
import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { useBookingDraft } from "../BookingDraftContext";
import { useStepCta } from "../useStepCta";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";
import { useTranslation } from "react-i18next";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";

// Helper: HH:mm from date + seconds (only if backend didn't give startStr)
function secToHHmm(dateISO: string, sec: number) {
  const dt = DateTime.fromISO(dateISO).plus({ seconds: sec });
  // use Luxon format; we avoid native Date per time-handling rules
  return dt.toFormat("HH:mm");
}

export default function SpecialistsViewContainer({
  onBack,
  onContinue,
  onGoToSpecialist,
  onGoToServices,
  onOpenClientDashboard,
  onOpenSupport,
  preselectedStaffId,
  onAutoAdvance,
}: {
  onBack: () => void;
  onContinue?: () => void;
  onGoToSpecialist: () => void;
  onGoToServices: () => void;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
  preselectedStaffId?: string | null;
  onAutoAdvance?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { draft, actions } = useBookingDraft();
  const cta = useStepCta(draft, "specialist", t);
  const ctaLabel = cta?.label ?? t("widget.services.choose", "Choose service");
  const sortLocale = i18n.resolvedLanguage || i18n.language || "en";
  const luxonLocale = getLuxonLocale(sortLocale);
  // --- time in salon tz (rules: use Company tz, not browser tz)
  const now = salonNow(); // salon local time
  const todayISO = now.toISODate();
  const tomorrowISO = now.plus({ days: 1 }).toISODate();
  const nowSec = Math.floor(now.diff(now.startOf("day")).as("seconds"));

  // Build date list for the next 2 months (inclusive)
  const dateList = React.useMemo(() => {
    const twoMonths = { months: 2 };
    const end = now.plus(twoMonths).startOf("day");
    const out: string[] = [];
    let d = now.startOf("day");
    while (d <= end) {
      out.push(d.toISODate());
      d = d.plus({ days: 1 });
    }
    return out;
  }, [now.toISO()]);

  // 1) Pull availability for the next ~2 months (PUBLIC via apiPath scope)
  const { data: availByDate, isLoading, error } = useAvailabilityForDates(dateList);

  // 2) Derive presentational list from availability rows + staff meta already in StaffRegistry
  const specialists = React.useMemo(() => {
    if (!availByDate) return [];

    const staffById = getStaffRegistry().getStaffByIdMap(); // read-only, no network (populated by the same availability response)

    // Aggregate by staff across the full 2-month window
    const out = new Map<number, {
      id: string;
      name: string;
      title: string;
      rating: number;
      reviewCount: number;
      avatar: string;
      availableSlots: { day: string; times: string[] }[];
      information: string;
      reviews: any[];
      _earliest: number | null; // for sorting
    }>();

    const MAX_TIMES_PER_DAY = 3;
    const MAX_DAYS_PER_STAFF = 2; // keep the card compact

    const labelForDate = (dateISO: string): string => {
      if (dateISO === todayISO) return t("widget.datetime.today", "today");
      if (dateISO === tomorrowISO) return t("widget.datetime.tomorrow", "tomorrow");
      return DateTime.fromISO(dateISO).setLocale(luxonLocale).toFormat("d LLL");
    };

    const pushDay = (dateISO: string) => {
      const label = labelForDate(dateISO);
      const rows = availByDate[dateISO] ?? [];
      for (const row of rows) {
        const staff = staffById[row.staffId];
        if (!staff) continue; // staff meta must exist; skip otherwise

        // cap per-staff number of day sections to keep UI small
        const existing = out.get(row.staffId);
        if (existing && existing.availableSlots.length >= MAX_DAYS_PER_STAFF) continue;

        const slots = (dateISO === todayISO)
          ? row.slots.filter(s => (s.start ?? 0) >= nowSec) // only future for today
          : row.slots;

        // Take the first N “closest” times per day
        const times = slots.slice(0, MAX_TIMES_PER_DAY).map(s => s.startStr ?? secToHHmm(dateISO, s.start));

        if (times.length === 0) continue;

        const entry = out.get(row.staffId) ?? {
          id: String(row.staffId),
          name: staff.name,
          title: staff.specialization || "",
          rating: staff.reviews?.rating_avg ?? 0,
          reviewCount: staff.reviews?.total ?? 0,
          avatar: staff.avatar || "",
          information: staff.information || "",
          reviews: staff.reviews?.items ?? [],
          availableSlots: [],
          _earliest: null,
        };

        entry.availableSlots.push({ day: label, times });

        // track earliest absolute time for sorting
        const first = slots[0];
        const abs = DateTime.fromISO(dateISO).startOf("day").plus({ seconds: first.start }).toJSDate().getTime();
        entry._earliest = entry._earliest == null ? abs : Math.min(entry._earliest, abs);

        out.set(row.staffId, entry);
      }
    };

    // Walk the whole window in chronological order
    for (const dISO of dateList) {
      pushDay(dISO);
    }

    // Waitlist (README §4.1 rewire): fully-booked masters are the PRIME
    // waitlist candidates — include them (zero slots) when it is active.
    const wl = getWaitlistConfig();
    const wlActive = !!wl?.active;
    if (wlActive) {
      const seenStaff = new Set<number>();
      for (const dISO of dateList) {
        for (const row of (availByDate[dISO] ?? [])) seenStaff.add(row.staffId);
      }
      for (const sid of seenStaff) {
        if (out.has(sid)) continue;
        const staff = staffById[sid];
        if (!staff) continue;
        out.set(sid, {
          id: String(sid),
          name: staff.name,
          title: staff.specialization || "",
          rating: staff.reviews?.rating_avg ?? 0,
          reviewCount: staff.reviews?.total ?? 0,
          avatar: staff.avatar || "",
          information: staff.information || "",
          reviews: staff.reviews?.items ?? [],
          availableSlots: [],
          _earliest: null,
        });
      }
    }

    // Only staff with any availableSlots (kept when waitlist is active)
    const arr = Array.from(out.values()).filter(
      x => wlActive || x.availableSlots.length > 0);

    // Sort by earliest slot across both days, then by name
    arr.sort((a, b) => {
      const wa = Number(staffById[Number(a.id)]?.weight);
      const wb = Number(staffById[Number(b.id)]?.weight);
      const hasWa = Number.isFinite(wa);
      const hasWb = Number.isFinite(wb);
      if (hasWa && hasWb && wa !== wb) return wb - wa; // heavier first
      if (hasWa && !hasWb) return -1;                   // prefer weighted over non-weighted
      if (!hasWa && hasWb) return 1;
      if (a._earliest != null && b._earliest != null && a._earliest !== b._earliest) {
        return a._earliest - b._earliest;
      }
      return a.name.localeCompare(b.name, sortLocale);
    });

    // Strip helper field + annotate waitlist eligibility per master
    const horizonMs = now.plus({ days: wl?.horizon_days ?? 7 })
      .endOf("day").toJSDate().getTime();
    const base = arr.map(({ _earliest, ...rest }) => ({
      ...rest,
      waitlistEligible:
        wlActive && (_earliest == null || _earliest > horizonMs),
    }));

    // Filter by required services selected in draft
    const requiredServiceIds = (draft.serviceIds ?? []).map(Number);
    let filteredArr = base;
    if (requiredServiceIds.length) {
      const allowed = new Set(getServiceRegistry().getMasterIdsOfferingAll(requiredServiceIds));
      filteredArr = filteredArr.filter((x) => allowed.has(Number(x.id)));
    }

    // Prepend "Любой специалист" to match dev design (only when there are real staff items)
    // This preserves the existing empty-state when no one has slots today/tomorrow.
    if (filteredArr.length > 0) {
      const anySpecialist = {
        id: "any",
        name: t("widget.specialists.any_specialist", "Any specialist"),
        title: "",
        rating: 0,
        reviewCount: 0,
        avatar: "",
        availableSlots: [] as { day: string; times: string[] }[],
      };
      return [anySpecialist, ...filteredArr];
    }

    return filteredArr;
  }, [availByDate, dateList, todayISO, tomorrowISO, nowSec, draft.serviceIds, luxonLocale, sortLocale, t]);

  // ── Staff preselection handling ──────────────────────────────────────
  const [preselectionHandled, setPreselectionHandled] = React.useState(false);
  const [staffNotFound, setStaffNotFound] = React.useState(false);
  const [staffFoundName, setStaffFoundName] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Only act once, only if there's a preselected ID, and only after data loaded
    if (preselectionHandled || !preselectedStaffId || isLoading || !specialists.length) return;

    // Check if the preselected staff exists in the loaded list (skip "any" virtual entry)
    const found = specialists.find(
      (s) => s.id !== "any" && s.id === preselectedStaffId,
    );

    setPreselectionHandled(true);

    if (found) {
      // Staff exists → select them and show success overlay (user clicks OK to advance)
      actions.setSpecialist(preselectedStaffId);
      setStaffFoundName(found.name);
    } else {
      // Staff not found → show error overlay on top of the faded list
      setStaffNotFound(true);
    }
  }, [preselectionHandled, preselectedStaffId, isLoading, specialists]);
  // ────────────────────────────────────────────────────────────────────

  // Loading / error / empty states keep the FULL widget chrome (header,
  // back, nav) — a bare message with no way out is a dead end.
  const StateShell = ({ children }: { children: React.ReactNode }) => (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          <WidgetHeader
            onBack={onBack}
            onGoToSpecialist={onGoToSpecialist}
            onGoToServices={onGoToServices}
            onOpenClientDashboard={onOpenClientDashboard}
            onOpenSupport={onOpenSupport}
          />
          <div className="flex-1 overflow-y-auto p-6">
            <div className="pb-2">
              <h2 className="text-2xl font-bold text-foreground">{t("widget.specialists.choose", "Choose specialist")}</h2>
            </div>
            {children}
          </div>
        </Card>
      </div>
    </div>
  );

  const [wlStaff, setWlStaff] = React.useState<{ id: number; name: string } | null>(null);

  if (isLoading) {
    return (
      <StateShell>
        <div className="py-10 text-center text-muted-foreground">{t("widget.specialists.loading_availability", "Loading available time...")}</div>
      </StateShell>
    );
  }
  if (error) {
    return (
      <StateShell>
        <div className="py-10 text-center text-muted-foreground">{t("widget.specialists.error_availability", "Could not load availability. Please try again later.")}</div>
      </StateShell>
    );
  }
  if (!specialists.length) {
    return (
      <StateShell>
        <div className="py-10 text-center">
          <div className="text-muted-foreground">{t("widget.specialists.no_slots", "No available slots in the next 2 months.")}</div>
          <Button className="mt-4 rounded-xl" variant="outline" onClick={onGoToServices}>
            {t("widget.specialists.try_services", "Browse services instead")}
          </Button>
        </div>
      </StateShell>
    );
  }

  return (
    <div className="relative h-full">
      {wlStaff && (
        <WaitlistDialog
          staffId={wlStaff.id}
          staffName={wlStaff.name}
          onClose={() => setWlStaff(null)}
        />
      )}
      {/* Specialists list — faded when any overlay is active */}
      <div className={(staffNotFound || staffFoundName) ? "opacity-25 pointer-events-none select-none" : undefined}>
        <SpecialistsView
          specialists={specialists}
          selectedId={draft.specialistId}
          onJoinWaitlist={(id, name) => setWlStaff({ id: Number(id), name })}
          onBack={onBack}
          onSelect={actions.setSpecialist}
          onContinue={onContinue}
          ctaLabel={ctaLabel}
          onGoToSpecialist={onGoToSpecialist}
          onGoToServices={onGoToServices}
          onOpenClientDashboard={onOpenClientDashboard}
          onOpenSupport={onOpenSupport}
        />
      </div>

      {/* "Staff selected" success overlay */}
      {staffFoundName && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/60">
          <div className="mx-6 max-w-sm rounded-2xl bg-card p-8 text-center shadow-xl border border-border">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">
              {t("widget.specialists.preselected_success_title", "Specialist selected")}
            </h3>
            <p className="mb-6 text-sm text-muted-foreground">
              {t(
                "widget.specialists.preselected_success_body",
                "You're booking with {{name}}. Choose the services you'd like.",
                { name: staffFoundName },
              )}
            </p>
            <button
              className="w-full rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={() => {
                setStaffFoundName(null);
                onAutoAdvance?.();
              }}
            >
              {t("widget.common.ok", "OK")}
            </button>
          </div>
        </div>
      )}

      {/* "Staff not found" error overlay */}
      {staffNotFound && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/60">
          <div className="mx-6 max-w-sm rounded-2xl bg-card p-8 text-center shadow-xl border border-border">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">
              {t("widget.specialists.preselected_not_found_title", "Specialist not found")}
            </h3>
            <p className="mb-6 text-sm text-muted-foreground">
              {t(
                "widget.specialists.preselected_not_found_body",
                "The requested specialist is not available. Please choose from the available specialists below.",
              )}
            </p>
            <button
              className="w-full rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={() => setStaffNotFound(false)}
            >
              {t("widget.specialists.select_available", "Select from available specialists")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

